import { message } from 'antd';
import { DashboardSidebar } from '@/common/components/Sidebar';
import { useStoreAuth } from '@/common/contexts/useAuth';
import { useSidebar } from '@/common/contexts/useSidebar';
import {
  getHRISAdminData,
  getHRISCompanyData,
} from '@/common/services/HRISService';
import { parseErrorMessage } from '@/common/utils';
import useSwitchPlatform from '@/modules/auth/hooks/useSwitchPlatform';

type PlatformSwitcherProps = {
  setRequestCompanyModal: (value: boolean) => void;
  setSuperAdminConfirmationModal: (value: boolean) => void;
};

type PlatformSwitcherKey = 'hris' | 'ats';

const PlatformSwitcher = ({
  setRequestCompanyModal,
  setSuperAdminConfirmationModal,
}: PlatformSwitcherProps) => {
  const DEFAULT_PLATFORM = 'ats';
  const { state } = useStoreAuth();
  const { redirectToHRIS } = useSwitchPlatform();
  const { setLoading } = useSidebar();

  const handleChangePlatform = async (key: PlatformSwitcherKey) => {
    setLoading(true);
    if (key === DEFAULT_PLATFORM) {
      return;
    }
    const responses = await Promise.allSettled([
      getHRISCompanyData({
        params: {
          email_domain_eq: state?.companyData?.company?.emailDomain,
        },
      }),
      getHRISAdminData({
        params: {
          user_id_eq: String(state?.companyData?.userId),
        },
      }),
    ]);

    const HRISCompanyData = responses[0];
    const HRISAdminData = responses[1];

    if (
      HRISCompanyData.status === 'rejected' &&
      HRISCompanyData.reason?.code !== 404
    ) {
      setLoading(false);
      const errorMessage = parseErrorMessage(HRISCompanyData.reason);
      message.error(errorMessage);
      return;
    }

    if (
      HRISAdminData.status === 'rejected' &&
      HRISAdminData.reason?.code !== 404
    ) {
      setLoading(false);
      const errorMessage = parseErrorMessage(HRISAdminData.reason);
      message.error(errorMessage);
      return;
    }

    // Company does not exist in HRIS
    if (HRISCompanyData.status === 'rejected') {
      setSuperAdminConfirmationModal(true);
      return;
    }

    // User is not part of the HRIS company
    if (
      HRISCompanyData.status === 'fulfilled' &&
      HRISAdminData.status === 'rejected'
    ) {
      setRequestCompanyModal(true);
    }

    // User is part of HRIS company
    if (
      HRISCompanyData.status === 'fulfilled' &&
      HRISAdminData.status === 'fulfilled'
    ) {
      // Redirect to destination
      redirectToHRIS();
    }
  };

  return (
    <DashboardSidebar.SwitchersContent>
      <DashboardSidebar.SwitcherItem
        variant="outlined"
        onClick={() => handleChangePlatform('hris')}
      >
        <DashboardSidebar.SwitcherItemKantorku />
      </DashboardSidebar.SwitcherItem>
      <DashboardSidebar.SwitcherItem variant="primary">
        <DashboardSidebar.SwitcherItemATS />
      </DashboardSidebar.SwitcherItem>
    </DashboardSidebar.SwitchersContent>
  );
};

export default PlatformSwitcher;
